import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, fmtMoney, formatErr, invalidateSaleQueries } from "@/lib/api";
import Modal, { inputCls, inputErrCls, FieldError, labelCls, btnAccent, btnPrimary, btnOutline } from "@/components/Modal";
import NumberInput from "@/components/NumberInput";
import { useFormErrors } from "@/lib/formErrors";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, Wallet, FileDown, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { downloadStatement } from "@/lib/pdf";

const toDateInput = (iso) => (iso ? new Date(iso).toLocaleDateString("en-CA", { timeZone: "Asia/Karachi" }) : "");
const todayInput = () => new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Karachi" });

export default function ClientProfile() {
  const { id } = useParams();
  const qc = useQueryClient();
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "manager";
  const [open, setOpen] = useState(false);
  const [editingPay, setEditingPay] = useState(null);   // payment being edited, or null = new
  const [amount, setAmount] = useState(0);
  const [method, setMethod] = useState("cash");
  const [notes, setNotes] = useState("");
  const [date, setDate] = useState("");

  const { data, isLoading } = useQuery({ queryKey: ["client", id], queryFn: () => api.get(`/clients/${id}`).then((r) => r.data) });

  const fe = useFormErrors();
  const pay = useMutation({
    mutationFn: () => {
      const body = { amount: parseFloat(amount), method, notes, date: date || null };
      return editingPay
        ? api.put(`/client-payments/${editingPay.id}`, body)
        : api.post(`/clients/${id}/payment`, { client_id: id, ...body });
    },
    onSuccess: () => {
      toast.success(editingPay ? "Payment updated" : "Payment recorded");
      invalidateSaleQueries(qc);
      ["client", "cash-daily", "cash-day-detail"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
      setOpen(false); setEditingPay(null); setAmount(0); setNotes(""); setDate(""); fe.clear();
    },
    onError: (e) => fe.handleError(e, toast.error),
  });
  const openReceive = () => { setEditingPay(null); fe.clear(); setAmount(0); setMethod("cash"); setNotes(""); setDate(todayInput()); setOpen(true); };
  const openEditPay = (p) => { setEditingPay(p); fe.clear(); setAmount(p.amount); setMethod(p.method || "cash"); setNotes(p.notes || ""); setDate(toDateInput(p.date || p.created_at)); setOpen(true); };
  const del = useMutation({
    mutationFn: (pid) => api.delete(`/client-payments/${pid}`),
    onSuccess: () => {
      toast.success("Payment deleted");
      invalidateSaleQueries(qc);
      ["client", "cash-daily", "cash-day-detail"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
    },
    onError: (e) => toast.error(formatErr(e.response?.data?.detail)),
  });

  if (isLoading || !data) return <div className="text-agri-ink-2">Loading…</div>;
  const { client, sales, payments } = data;

  return (
    <div className="space-y-6" data-testid="client-profile">
      <Link to="/clients" className="inline-flex items-center gap-1 text-sm text-agri-ink-2 hover:text-agri-forest"><ArrowLeft size={14} /> All clients</Link>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="card-soft p-6 lg:col-span-2">
          <div className="label-cap">Client</div>
          <h1 className="font-heading text-3xl mt-1">{client.name}</h1>
          <div className="grid grid-cols-2 gap-4 mt-5 text-sm">
            <div><div className="label-cap">Phone</div><div className="mt-1">{client.phone || "—"}</div></div>
            <div><div className="label-cap">Area</div><div className="mt-1">{client.area || "—"}</div></div>
            <div className="col-span-2"><div className="label-cap">Address</div><div className="mt-1">{client.address || "—"}</div></div>
            {client.cnic && <div><div className="label-cap">CNIC</div><div className="mt-1 font-mono">{client.cnic}</div></div>}
            <div><div className="label-cap">Credit Limit</div><div className="mt-1 font-semibold tabular-nums">{fmtMoney(client.credit_limit)}</div></div>
          </div>
          {client.notes && <div className="mt-4 p-3 bg-agri-bg rounded-lg text-sm text-agri-ink-2">{client.notes}</div>}
        </div>

        <div className="card-soft p-6">
          {client.balance > 0.001 ? (
            <>
              <div className="label-cap">Outstanding Balance</div>
              <div className="mt-3 text-4xl font-bold tabular-nums text-agri-danger">{fmtMoney(client.balance)}</div>
              <div className="text-xs text-agri-ink-2 mt-1">This customer owes you</div>
            </>
          ) : client.balance < -0.001 ? (
            <>
              <div className="label-cap">Advance / Store Credit</div>
              <div className="mt-3 text-4xl font-bold tabular-nums text-agri-forest">{fmtMoney(-client.balance)}</div>
              <div className="text-xs text-agri-ink-2 mt-1">Credit held for the customer · applies to their next credit sales</div>
            </>
          ) : (
            <>
              <div className="label-cap">Balance</div>
              <div className="mt-3 text-4xl font-bold tabular-nums text-agri-forest">{fmtMoney(0)}</div>
              <div className="text-xs text-agri-ink-2 mt-1">Settled</div>
            </>
          )}
          <button data-testid="receive-payment-btn" onClick={openReceive} className={btnAccent + " w-full mt-5 flex items-center justify-center gap-2"}>
            <Wallet size={16} /> Receive Payment
          </button>
          <button
            data-testid="download-statement-btn"
            onClick={() => downloadStatement(client)}
            className={btnOutline + " w-full mt-2 flex items-center justify-center gap-2"}
          >
            <FileDown size={16} /> Export Statement (PDF)
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="card-soft p-5">
          <h3 className="font-heading text-xl mb-3">Sales</h3>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {sales.length === 0 && <div className="text-sm text-agri-ink-2">No sales recorded.</div>}
            {sales.map((s) => (
              <div key={s.id} className="flex justify-between text-sm p-2.5 border border-[rgba(28,28,30,0.04)] rounded-lg">
                <div>
                  <div className="font-mono text-xs text-agri-ink-2">{s.invoice_no}</div>
                  <div className="text-xs">{new Date(s.created_at).toLocaleString()} · {s.payment_method}</div>
                </div>
                <div className="text-right">
                  <div className="font-semibold tabular-nums">{fmtMoney(s.total)}</div>
                  {s.credit_amount > 0 && <div className="text-xs text-agri-danger tabular-nums">+{fmtMoney(s.credit_amount)} credit</div>}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="card-soft p-5">
          <h3 className="font-heading text-xl mb-3">Payments received</h3>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {payments.length === 0 && <div className="text-sm text-agri-ink-2">No payments yet.</div>}
            {payments.map((p) => (
              <div key={p.id} className="flex justify-between items-center text-sm p-2.5 border border-[rgba(28,28,30,0.04)] rounded-lg">
                <div className="min-w-0">
                  <div className="text-xs text-agri-ink-2">{new Date(p.date || p.created_at).toLocaleDateString()}</div>
                  <div className="text-xs capitalize">{p.method} · {p.notes || "—"}</div>
                </div>
                <div className="flex items-center gap-2 ml-2">
                  <div className="font-semibold text-agri-forest tabular-nums whitespace-nowrap">-{fmtMoney(p.amount)}</div>
                  {canEdit && (
                    <>
                      <button title="Edit payment" onClick={() => openEditPay(p)} className="p-1 rounded hover:bg-black/5 text-agri-ink-2 hover:text-agri-forest">
                        <Pencil size={14} />
                      </button>
                      <button title="Delete payment" onClick={() => window.confirm(`Delete this ${fmtMoney(p.amount)} payment? ${client.name}'s balance will go back up by that amount.`) && del.mutate(p.id)} className="p-1 rounded hover:bg-red-50 text-agri-ink-2 hover:text-agri-danger">
                        <Trash2 size={14} />
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <Modal open={open} onClose={() => { setOpen(false); setEditingPay(null); }} title={editingPay ? `Edit payment · ${client.name}` : `Receive payment from ${client.name}`}>
        <form onSubmit={(e) => { e.preventDefault(); pay.mutate(); }} className="space-y-3">
          <div>
            <label className={labelCls}>Amount</label>
            <NumberInput data-testid="payment-amount-input" required value={amount}
              onChange={(n) => { setAmount(n); fe.clearField("amount"); }}
              className={fe.fieldError("amount") ? inputErrCls : ""} />
            <FieldError>{fe.fieldError("amount")}</FieldError>
            {!editingPay && (parseFloat(amount) || 0) > Math.max(client.balance || 0, 0) + 0.001 && (
              <p className="text-[11px] text-agri-forest mt-1">
                {fmtMoney((parseFloat(amount) || 0) - Math.max(client.balance || 0, 0))} more than owed → kept as advance / store credit for {client.name}.
              </p>
            )}
          </div>
          <div><label className={labelCls}>Date received</label>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={inputCls} /></div>
          <div><label className={labelCls}>Method</label>
            <select value={method} onChange={(e) => setMethod(e.target.value)} className={inputCls}>
              <option value="cash">Cash</option><option value="bank">Bank Transfer</option><option value="cheque">Cheque</option>
            </select></div>
          <div><label className={labelCls}>Notes</label><input value={notes} onChange={(e) => setNotes(e.target.value)} className={inputCls} /></div>
          <div className="flex justify-end gap-2 pt-2"><button type="button" onClick={() => { setOpen(false); setEditingPay(null); }} className={btnOutline}>Cancel</button><button data-testid="payment-save-btn" disabled={pay.isPending} className={btnPrimary}>{editingPay ? "Save changes" : "Record payment"}</button></div>
        </form>
      </Modal>
    </div>
  );
}
